import {KatGrid, KatSpinner} from "@amzn/katal-react";
import ReviewDecision from "src/app/component/review/ReviewDecision";
import * as React from "react";
import {useEffect, useState} from "react";
import {GetReviewResponseData} from "src/app/api/model/response/GetReviewResponse";
import {layoutRegistry} from "src/app/config/LayoutRegistry";
import ReviewSection from "src/app/component/review/ReviewSection";
import {observer} from "mobx-react";
import {v4 as uuid} from "uuid";
import {StringUtil} from "src/app/util/StringUtil";
import {Validator} from "src/app/util/Validator";
import reviewEventBus from "src/app/eventbus/ReviewEventBus";
import ReviewDecisionStore from "src/app/store/ReviewDecisionStore";
import reviewDecisionStore from "src/app/store/ReviewDecisionStore";
import ConfirmationDialog from "src/app/component/dialog/ConfirmationDialog";
import {ApiUtil} from "src/app/util/ApiUtil";
import {ReviewEvent, ReviewResult, ReviewStatus, ReviewType} from "src/app/constant/Enums";
import {EEPApi} from "src/app/api/EEPApi";
import {CASE_DECISION_REASON_CODE_CONFIG, DEFAULT_REASON_CODE_OPTION} from "src/app/config/DropDownConfig";
import ReOpenInputDialog from "src/app/component/dialog/ReOpenInputDialog";

interface ReviewProps {
    reviewId: string;
}

const Review = observer((props: ReviewProps) => {
    const [data, setData] = useState<GetReviewResponseData | null>(null);
    const [reasonCodes, setReasonCodes] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [startTime, setStartTime] = useState<string>(new Date().toISOString());
    const key = uuid();

    useEffect(() => {
        const startTime = new Date().toISOString();
        setStartTime(startTime);
        fetchData().catch(error => {
            console.error("Error fetching review data:", JSON.stringify(error));
            console.log(error.message);
            console.log(error.response?.data?.message);
            if (error.response?.data?.message != undefined) {
                alert(error.response?.data?.message);
            } else {
                alert("Error while fetching review data:" + error.message + " Please try again later!");
            }
            reviewEventBus.emit(ReviewEvent.CLOSE_REVIEW, true);
        }).finally(() => {
            setLoading(false);
        });

        reviewEventBus.on(ReviewEvent.SUBMIT_REVIEW_DECISION, handleDecisionSubmit);
        reviewEventBus.on(ReviewEvent.SUBMIT_REVIEW, handleConfirmSubmit);
        return () => {
            reviewEventBus.removeListener(ReviewEvent.SUBMIT_REVIEW_DECISION, handleDecisionSubmit);
            reviewEventBus.removeListener(ReviewEvent.SUBMIT_REVIEW, handleConfirmSubmit);
        };
    }, []);

    const fetchData = async () => {
        if (data == null) {
            const response = await EEPApi.getReview(ApiUtil.createGetReviewRequest(props.reviewId));
            setData(response.data);
            setReasonCodes(CASE_DECISION_REASON_CODE_CONFIG[response.data.caseType] == undefined ? DEFAULT_REASON_CODE_OPTION :
                CASE_DECISION_REASON_CODE_CONFIG[response.data.caseType]);
        }
    };

    const handleDecisionSubmit = (event: any) => {
        if (event) {
            validateDecision();
        }
    };

    const handleConfirmSubmit = async (event: any) => {
        if (event) {
            await submitReview();
        }
    };

    const validateDecision = () => {
        const errMsg = Validator.validateDecision(reviewDecisionStore.value);
        if (StringUtil.isNullOrEmpty(errMsg)) {
            if (ReviewResult.RE_OPENED == reviewDecisionStore.value.reviewResult) {
                reviewEventBus.emit(ReviewEvent.SHOW_REVIEW_REOPEN_CONFIRMATION_DIALOG, true);
            } else {
                reviewEventBus.emit(ReviewEvent.SHOW_REVIEW_CONFIRMATION_DIALOG, true);
            }
        } else {
            alert(errMsg);
        }
    }

    const submitReview = async () => {
        try {
            const endTime = new Date().toISOString();
            const request = ApiUtil.createUpdateReviewRequest(props.reviewId, ReviewDecisionStore.value, startTime, endTime);
            console.log("request is: " + JSON.stringify(request));
            const result = await EEPApi.updateReview(request);
            if (ReviewResult.RE_OPENED == request.reviewResult) {
                alert("Has kick off the system to reopen the review successfully, please check the REOPEN_AUDIT queue later!");
            }
            reviewEventBus.emit(ReviewEvent.CLOSE_REVIEW, true);
        } catch (error: any) {
            console.error("Error while submitting review decision:", error);
            console.log(error.message);
            console.log(error.response?.data?.message);
            if (error.response?.data?.message != undefined) {
                alert(error.response?.data?.message);
            } else {
                alert("Error while submitting review decision:" + error.message + " Please try again later!");
            }
        }
    }

    return (
        <React.Suspense>
            {loading ? (
                <div className={'table-center'}>
                    <KatSpinner size="large"/>
                </div>
            ) : (
                data != null && (() => {
                    const CustomLayout = layoutRegistry[data.uiLayoutId];
                    return CustomLayout
                        ? <CustomLayout reviewId={props.reviewId} data={data} />
                        : (
                <div>
                    <div className="katal container review-sections-container">
                        <KatGrid variant={'card'} density={'low'} colsL={2} colsM={6} colsS={7} colsXl={2} colsXxl={2}
                                 className='full-height'>
                            {data?.reviewSections.map((sectionData, index) => (
                                <ReviewSection data={sectionData} key={key + index}
                                               data-testid="review-section"></ReviewSection>
                            ))
                            }
                        </KatGrid>
                    </div>
                    <div className='review-decision-container'>
                        <ReviewDecision slot="footer" reasonCodes={reasonCodes}
                                        reviewStatus={data?.reviewStatus || ReviewStatus.PENDING}
                                        reviewType={data?.reviewType || ReviewType.PRIMARY_AUDIT}
                                        reviewResult={data?.reviewResult || ReviewResult.PENDING}
                                        reOpenFor={data.reOpenFor}
                                        lastAuditor={data.lastModifiedBy}
                                        decisionDetail={data?.lastReviewDecisionDetail}
                                        caseSubType={data.caseSubType}
                                        data-testid='review-decision'></ReviewDecision>
                    </div>
                    <ConfirmationDialog openDialogEvent={ReviewEvent.SHOW_REVIEW_CONFIRMATION_DIALOG}
                                        closeDialogEvent={ReviewEvent.SUBMIT_REVIEW}
                                        eventBus={reviewEventBus}
                                        content={'Do you confirm to submit the review decision?'}
                                        data-testid="confirmation-dialog"
                    ></ConfirmationDialog>
                    <ReOpenInputDialog openDialogEvent={ReviewEvent.SHOW_REVIEW_REOPEN_CONFIRMATION_DIALOG}
                                        closeDialogEvent={ReviewEvent.SUBMIT_REVIEW}
                                        eventBus={reviewEventBus}
                                        data-testid="reopen-input-dialog"
                    ></ReOpenInputDialog>
                </div>
                    );
                })()
            )}
        </React.Suspense>
    );
});

export default Review;
